/**
 * Normalizes catalog artwork in the browser before it is uploaded to R2.
 *
 * Two constraints shape this. The app decodes catalog art with `UIImage(data:)`, which
 * cannot read SVG — a published vector renders here and stays blank on device. And every
 * asset is fetched over a phone connection and then cached on device, so artwork is
 * rendered at the resolution the app actually draws rather than whatever the source
 * file happened to be.
 */

export type CatalogImageKind = "card" | "logo" | "institution" | "network" | "merchant";

export type CatalogUploadOptions = {
  /** Merchant only: rasterize/resize but skip white knockout and recolor. */
  merchantPreserveColors?: boolean;
};

/**
 * Longest edge in device pixels: 3x Retina at the largest size the app draws each asset
 * (logo tiles ~36pt, merchant icons up to 56pt, card faces the full screen width).
 *
 * Small brand marks stay PNG. They are only a few KB at this size, stay pixel-exact on
 * the tinted tiles they sit on, and merchant icons are drawn as template images — lossy
 * alpha would fray the knocked-out glyph edges. Card faces are large and detailed
 * enough that WebP is worth it.
 */
const BUDGETS: Record<CatalogImageKind, { maxSize: number; preferWebP: boolean }> = {
  card: { maxSize: 1200, preferWebP: true },
  logo: { maxSize: 256, preferWebP: false },
  institution: { maxSize: 256, preferWebP: false },
  network: { maxSize: 256, preferWebP: false },
  merchant: { maxSize: 256, preferWebP: false },
};

const WEBP_QUALITY = 0.9;
const JPEG_QUALITY = 0.88;
/** Only change format when the saving is worth losing PNG's exactness. */
const MIN_ALTERNATE_SAVING = 0.15;

export function isSvgFile(file: File): boolean {
  return file.type === "image/svg+xml" || /\.svgz?$/i.test(file.name);
}

/**
 * Turns white shapes into holes (regex fallback when DOM parsing fails).
 */
export function knockOutWhiteFills(svgText: string): string {
  const white = whiteColorPattern();
  return svgText
    .replace(new RegExp(String.raw`(fill\s*=\s*["'])(?:${white})(["'])`, "gi"), "$1none$2")
    .replace(new RegExp(String.raw`(stroke\s*=\s*["'])(?:${white})(["'])`, "gi"), "$1none$2")
    .replace(
      new RegExp(String.raw`(style\s*=\s*["'][^"']*?fill\s*:\s*)(?:${white})`, "gi"),
      "$1none",
    )
    .replace(
      new RegExp(String.raw`(style\s*=\s*["'][^"']*?stroke\s*:\s*)(?:${white})`, "gi"),
      "$1none",
    );
}

function whiteColorPattern(): string {
  return String.raw`#fff(?:fff)?|white|rgb\(\s*255\s*,\s*255\s*,\s*255\s*\)|rgba\(\s*255\s*,\s*255\s*,\s*255\s*(?:,\s*[\d.]+\s*)?\)`;
}

type Rgb = { r: number; g: number; b: number };

function clamp255(value: number): number {
  return Math.max(0, Math.min(255, Math.round(value)));
}

/** Parses common SVG/CSS color literals used in catalog SVG uploads. */
export function parseSvgColor(value: string | null | undefined): Rgb | null {
  if (!value) return null;
  const raw = value.trim().replace(/\s+/g, " ");
  if (!raw || raw === "none" || raw === "transparent") return null;

  const lower = raw.toLowerCase();
  if (lower === "currentcolor") return { r: 0, g: 0, b: 0 };
  if (lower === "white") return { r: 255, g: 255, b: 255 };
  if (lower === "black") return { r: 0, g: 0, b: 0 };

  let match = /^#([0-9a-f]{3})$/i.exec(raw);
  if (match) {
    const [r, g, b] = match[1]!.split("").map((digit) => parseInt(`${digit}${digit}`, 16));
    return { r: r!, g: g!, b: b! };
  }

  match = /^#([0-9a-f]{6})$/i.exec(raw);
  if (match) {
    const hex = match[1]!;
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
    };
  }

  match =
    /^rgba?\(\s*([\d.]+%?)\s*,\s*([\d.]+%?)\s*,\s*([\d.]+%?)(?:\s*,\s*[\d.]+\s*)?\)$/i.exec(raw);
  if (match) {
    const channel = (part: string) => (part.endsWith("%") ? (parseFloat(part) / 100) * 255 : parseFloat(part));
    return {
      r: clamp255(channel(match[1]!)),
      g: clamp255(channel(match[2]!)),
      b: clamp255(channel(match[3]!)),
    };
  }

  return null;
}

export function isWhiteSvgColor(value: string | null | undefined): boolean {
  const rgb = parseSvgColor(value);
  if (!rgb) return false;
  return isNearWhiteRgb(rgb.r, rgb.g, rgb.b);
}

function isNearWhiteRgb(r: number, g: number, b: number): boolean {
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  const maxC = Math.max(r, g, b);
  const minC = Math.min(r, g, b);
  const saturation = maxC === 0 ? 0 : (maxC - minC) / maxC;
  return luminance >= 0.9 && saturation <= 0.12;
}

function rewriteInlineStyle(style: string): string {
  const rewritten = style
    .split(";")
    .map((decl) => {
      const trimmed = decl.trim();
      if (!trimmed) return "";
      const match = /^(fill|stroke)\s*:\s*(.+)$/i.exec(trimmed);
      if (!match) return trimmed;
      const prop = match[1]!.toLowerCase();
      const color = match[2]!.trim();
      if (isWhiteSvgColor(color)) return `${prop}: none`;
      if (parseSvgColor(color)) return `${prop}: #000000`;
      return trimmed;
    })
    .filter(Boolean)
    .join("; ");
  return rewritten;
}

function normalizeMerchantStyleBlock(css: string): string {
  return css.replace(
    /\b(fill|stroke)\s*:\s*([^;}{]+)/gi,
    (full, prop: string, color: string) => {
      const trimmed = color.trim();
      if (isWhiteSvgColor(trimmed)) return `${prop}: none`;
      if (parseSvgColor(trimmed)) return `${prop}: #000000`;
      return full;
    },
  );
}

function normalizeMerchantPaint(el: Element, attr: "fill" | "stroke"): void {
  const value = el.getAttribute(attr);
  if (!value) return;
  if (isWhiteSvgColor(value)) {
    el.setAttribute(attr, "none");
    return;
  }
  if (parseSvgColor(value)) el.setAttribute(attr, "#000000");
}

function normalizeMerchantInlineStyle(el: Element): void {
  const style = el.getAttribute("style");
  if (!style) return;
  el.setAttribute("style", rewriteInlineStyle(style));
}

/**
 * DOM-aware merchant SVG prep: white paint → holes, every other explicit color →
 * black ink before rasterization. Keeps inherited/default paths on black so the
 * pixel pass can reliably knock white out and re-tint ink to white.
 */
export function prepareMerchantSvg(svgText: string): string {
  if (typeof DOMParser === "undefined") return knockOutWhiteFills(svgText);

  try {
    const doc = new DOMParser().parseFromString(svgText, "image/svg+xml");
    if (doc.querySelector("parsererror")) return knockOutWhiteFills(svgText);

    const root = doc.documentElement;
    if (root.tagName.toLowerCase() !== "svg") return knockOutWhiteFills(svgText);

    root.setAttribute("color", "#000000");
    if (!root.getAttribute("fill")) root.setAttribute("fill", "#000000");

    for (const styleEl of root.querySelectorAll("style")) {
      styleEl.textContent = normalizeMerchantStyleBlock(styleEl.textContent ?? "");
    }

    const elements = [root, ...Array.from(root.querySelectorAll("*"))];
    for (const el of elements) {
      const stroke = el.getAttribute("stroke");
      const hasStroke = Boolean(stroke && stroke !== "none");
      if (hasStroke && !el.hasAttribute("fill")) el.setAttribute("fill", "none");
      normalizeMerchantPaint(el, "fill");
      normalizeMerchantPaint(el, "stroke");
      normalizeMerchantInlineStyle(el);
    }

    return new XMLSerializer().serializeToString(doc);
  } catch {
    return knockOutWhiteFills(svgText);
  }
}

/** Pixels this bright and desaturated are treated as cutout holes, not glyph ink. */
const MERCHANT_WHITE_LUMINANCE = 0.9;
const MERCHANT_WHITE_SATURATION = 0.12;

/**
 * Bakes merchant marks as white-on-transparent PNGs.
 *
 * Near-white pixels become fully transparent (cutouts inside a filled shape).
 * Every other visible pixel is forced to white while preserving its alpha so
 * anti-aliased edges stay smooth. The app also template-tints these marks, but
 * normalizing at upload keeps admin previews, re-imports, and any non-template
 * draw paths consistent.
 */
export function normalizeMerchantIconCanvas(canvas: HTMLCanvasElement): void {
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) throw new Error("Canvas unavailable for merchant icon normalization.");

  const { width, height } = canvas;
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i]!;
    const g = data[i + 1]!;
    const b = data[i + 2]!;
    const a = data[i + 3]!;
    if (a === 0) continue;

    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    const maxC = Math.max(r, g, b);
    const minC = Math.min(r, g, b);
    const saturation = maxC === 0 ? 0 : (maxC - minC) / maxC;

    if (luminance >= MERCHANT_WHITE_LUMINANCE && saturation <= MERCHANT_WHITE_SATURATION) {
      data[i + 3] = 0;
      continue;
    }

    data[i] = 255;
    data[i + 1] = 255;
    data[i + 2] = 255;
  }

  ctx.putImageData(imageData, 0, 0);
}

const MERCHANT_VISIBLE_ALPHA = 12;
const MERCHANT_WHITE_INK_LUMINANCE = 0.88;
const MERCHANT_WHITE_INK_SATURATION = 0.14;
const MERCHANT_WHITE_INK_CHANNEL = 220;

/**
 * True when every visible pixel is already near-white (typical white-on-transparent
 * merchant mark). Used to skip recoloring hand-authored PNGs.
 */
export function isAlreadyWhiteOnTransparent(canvas: HTMLCanvasElement): boolean {
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return false;

  const { width, height } = canvas;
  const data = ctx.getImageData(0, 0, width, height).data;
  let visiblePixels = 0;

  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3]!;
    if (a < MERCHANT_VISIBLE_ALPHA) continue;

    visiblePixels += 1;
    const r = data[i]!;
    const g = data[i + 1]!;
    const b = data[i + 2]!;

    if (r < MERCHANT_WHITE_INK_CHANNEL || g < MERCHANT_WHITE_INK_CHANNEL || b < MERCHANT_WHITE_INK_CHANNEL) {
      return false;
    }

    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    const maxC = Math.max(r, g, b);
    const minC = Math.min(r, g, b);
    const saturation = maxC === 0 ? 0 : (maxC - minC) / maxC;
    if (luminance < MERCHANT_WHITE_INK_LUMINANCE || saturation > MERCHANT_WHITE_INK_SATURATION) {
      return false;
    }
  }

  return visiblePixels > 0;
}

function shouldNormalizeMerchantCanvas(
  canvas: HTMLCanvasElement,
  options?: CatalogUploadOptions,
): boolean {
  if (options?.merchantPreserveColors) return false;
  return !isAlreadyWhiteOnTransparent(canvas);
}

function merchantBitmapEncodeOptions(): { preferWebP: boolean; allowJpeg: boolean } {
  return { preferWebP: false, allowJpeg: false };
}

function prepareMerchantBitmap(
  bitmap: ImageBitmap,
  file: File,
  budget: { maxSize: number; preferWebP: boolean },
  options?: CatalogUploadOptions,
): Promise<File> {
  const withinBudget = bitmap.width <= budget.maxSize && bitmap.height <= budget.maxSize;

  if (options?.merchantPreserveColors) {
    if (withinBudget) return Promise.resolve(file);
    const { width, height } = fit(bitmap.width, bitmap.height, budget.maxSize);
    const canvas = draw(bitmap, width, height);
    return encode(canvas, baseName(file), merchantBitmapEncodeOptions());
  }

  const { width, height } = withinBudget
    ? { width: bitmap.width, height: bitmap.height }
    : fit(bitmap.width, bitmap.height, budget.maxSize);
  const canvas = draw(bitmap, width, height);
  const preserve = !shouldNormalizeMerchantCanvas(canvas, options);

  if (!preserve) normalizeMerchantIconCanvas(canvas);

  if (withinBudget && preserve) return Promise.resolve(file);

  return encode(canvas, baseName(file), merchantBitmapEncodeOptions());
}

/** True for already-published art the app cannot draw, so the admin can re-import it. */
export function isVectorAssetURL(url: string | null | undefined): boolean {
  if (!url) return false;
  return /\.svgz?(\?|#|$)/i.test(url);
}

/**
 * Returns upload-ready raster bytes: vector art is rendered at the app's drawing size,
 * oversized bitmaps are downscaled, and anything already within budget is left alone
 * rather than recompressed.
 */
export async function prepareCatalogUpload(
  file: File,
  kind: CatalogImageKind,
  options?: CatalogUploadOptions,
): Promise<File> {
  const budget = BUDGETS[kind];

  if (isSvgFile(file)) {
    const svgText = await file.text();
    const preserveMerchant = kind === "merchant" && options?.merchantPreserveColors;
    const prepared =
      kind === "merchant" && !preserveMerchant ? prepareMerchantSvg(svgText) : svgText;
    return renderSvg(prepared, baseName(file), budget, kind, options);
  }

  const bitmap = await decodeBitmap(file);
  try {
    if (kind === "merchant") {
      return await prepareMerchantBitmap(bitmap, file, budget, options);
    }

    const withinBudget = bitmap.width <= budget.maxSize && bitmap.height <= budget.maxSize;
    if (withinBudget) return file;

    const { width, height } = fit(bitmap.width, bitmap.height, budget.maxSize);
    const canvas = draw(bitmap, width, height);
    return encode(canvas, baseName(file), {
      preferWebP: budget.preferWebP || file.type === "image/jpeg",
      allowJpeg: file.type === "image/jpeg",
    });
  } finally {
    bitmap.close();
  }
}

/** Rasterizes SVG markup on its own, for callers that already hold the markup. */
export async function renderSvgToFile(
  svgText: string,
  name: string,
  kind: CatalogImageKind,
  options?: CatalogUploadOptions,
): Promise<File> {
  const preserveMerchant = kind === "merchant" && options?.merchantPreserveColors;
  const prepared = kind === "merchant" && !preserveMerchant ? prepareMerchantSvg(svgText) : svgText;
  return renderSvg(prepared, name, BUDGETS[kind], kind, options);
}

async function renderSvg(
  svgText: string,
  name: string,
  budget: { maxSize: number; preferWebP: boolean },
  kind: CatalogImageKind,
  options?: CatalogUploadOptions,
): Promise<File> {
  const objectUrl = URL.createObjectURL(new Blob([svgText], { type: "image/svg+xml;charset=utf-8" }));
  try {
    const img = await loadImage(objectUrl);
    const intrinsic = intrinsicSize(img, svgText);
    const { width, height } = fit(intrinsic.width, intrinsic.height, budget.maxSize);
    const canvas = draw(img, width, height);
    if (kind === "merchant" && shouldNormalizeMerchantCanvas(canvas, options)) {
      normalizeMerchantIconCanvas(canvas);
    }
    return encode(canvas, name, {
      preferWebP: budget.preferWebP && kind !== "merchant",
      allowJpeg: false,
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/**
 * Picks the smallest encoding that is worth using. PNG is the floor: it always works,
 * keeps alpha exact, and wins outright for flat marks. A browser that cannot encode a
 * requested format quietly returns PNG, so each candidate's type is verified.
 */
async function encode(
  canvas: HTMLCanvasElement,
  name: string,
  options: { preferWebP: boolean; allowJpeg: boolean },
): Promise<File> {
  const png = await toBlob(canvas, "image/png");
  let best = png;

  const alternates: Array<[string, number]> = [];
  if (options.preferWebP) alternates.push(["image/webp", WEBP_QUALITY]);
  if (options.allowJpeg) alternates.push(["image/jpeg", JPEG_QUALITY]);

  for (const [mime, quality] of alternates) {
    const candidate = await toBlob(canvas, mime, quality).catch(() => null);
    if (!candidate || candidate.type !== mime) continue;
    if (candidate.size < best.size * (1 - MIN_ALTERNATE_SAVING)) best = candidate;
  }

  return new File([best], `${name}.${extensionFor(best.type)}`, { type: best.type });
}

function draw(
  source: CanvasImageSource,
  width: number,
  height: number,
): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas unavailable for image conversion.");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.clearRect(0, 0, width, height);
  ctx.drawImage(source, 0, 0, width, height);
  return canvas;
}

/** Scales to the budget's longest edge, preserving aspect ratio. Never upscales bitmaps. */
function fit(width: number, height: number, maxSize: number) {
  const longestEdge = Math.max(width, height);
  const scale = longestEdge > 0 ? maxSize / longestEdge : 1;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/**
 * Some browsers report a zero intrinsic size for an SVG with no width/height
 * attributes, so the viewBox is the fallback source of the aspect ratio.
 */
function intrinsicSize(img: HTMLImageElement, svgText: string) {
  if (img.naturalWidth > 0 && img.naturalHeight > 0) {
    return { width: img.naturalWidth, height: img.naturalHeight };
  }
  const viewBox = /viewBox\s*=\s*["']\s*[\d.+-]+[\s,]+[\d.+-]+[\s,]+([\d.+-]+)[\s,]+([\d.+-]+)/i.exec(
    svgText,
  );
  if (viewBox) {
    const width = Number(viewBox[1]);
    const height = Number(viewBox[2]);
    if (width > 0 && height > 0) return { width, height };
  }
  return { width: 512, height: 512 };
}

function toBlob(canvas: HTMLCanvasElement, mime: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error(`Could not encode ${mime}.`))),
      mime,
      quality,
    );
  });
}

async function decodeBitmap(file: File): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file);
  } catch {
    throw new Error("Could not read that image file.");
  }
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to decode SVG for conversion."));
    img.src = url;
  });
}

function baseName(file: File): string {
  return file.name.replace(/\.[^.]+$/, "") || "artwork";
}

function extensionFor(mime: string): string {
  switch (mime) {
    case "image/webp":
      return "webp";
    case "image/jpeg":
      return "jpg";
    default:
      return "png";
  }
}
